import React, { useState, useEffect } from "react";
import { Form, Button, Container, Row, Col, Table, Modal, InputGroup, Pagination } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CarForm = () => {
  // State variables
  const [cars, setCars] = useState([]);
  const [filteredCars, setFilteredCars] = useState([]);
  const [carName, setCarName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [editCarId, setEditCarId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [carToDelete, setCarToDelete] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  // API base URL
  const API_BASE_URL = "https://motor-easy.clickbooster.io/api/cars";

  // Fetch all cars on component mount
  useEffect(() => {
    fetchCars();
  }, []);

  // Filter cars when search term changes
  useEffect(() => {
    const filtered = cars.filter(car => 
      car.car_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCars(filtered);
    setCurrentPage(1); // Reset to first page on search
  }, [searchTerm, cars]);

  // Fetch all cars from API
  const fetchCars = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_BASE_URL);
      if (response.ok) {
        const data = await response.json();
        // Sort by id descending to show latest added cars first
        const sortedData = data.sort((a, b) => b.id - a.id);
        setCars(sortedData);
        setFilteredCars(sortedData);
      } else {
        toast.error("Failed to fetch cars");
      }
    } catch (error) {
      toast.error(`Error fetching cars: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission (Create or Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!carName.trim()) {
      toast.warning("Car name cannot be empty");
      return;
    }

    try {
      let response;
      
      if (isEditing) {
        // Update existing car
        response = await fetch(`${API_BASE_URL}/${editCarId}?car_name=${encodeURIComponent(carName)}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ car_name: carName }),
        });
      } else {
        // Create new car
        response = await fetch(API_BASE_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ car_name: carName }),
        });
      }

      if (response.ok) {
        toast.success(isEditing ? "Car updated successfully!" : "Car added successfully!");
        setCarName("");
        setIsEditing(false);
        setEditCarId(null);
        fetchCars();
      } else {
        const errorData = await response.text();
        toast.error(`Error: ${errorData}`);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  // Handle edit button click
  const handleEdit = (car) => {
    setCarName(car.car_name);
    setEditCarId(car.id);
    setIsEditing(true);
  };

  // Handle delete confirmation
  const confirmDelete = (car) => {
    setCarToDelete(car);
    setShowDeleteModal(true);
  };

  // Handle actual deletion
  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/${carToDelete.id}?car_name=${encodeURIComponent(carToDelete.car_name)}`, {
        method: "DELETE",
      });

      if (response.ok) {
        toast.success("Car deleted successfully!");
        fetchCars();
      } else {
        const errorData = await response.text();
        toast.error(`Error deleting car: ${errorData}`);
      }
    } catch (error) {
      toast.error(`Error deleting car: ${error.message}`);
    } finally {
      setShowDeleteModal(false);
      setCarToDelete(null);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setCarName("");
    setIsEditing(false);
    setEditCarId(null);
  };

  // Handle search change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Pagination logic
  const indexOfLastCar = currentPage * itemsPerPage;
  const indexOfFirstCar = indexOfLastCar - itemsPerPage;
  const currentCars = filteredCars.slice(indexOfFirstCar, indexOfLastCar);
  const totalPages = Math.ceil(filteredCars.length / itemsPerPage);

  // Generate pagination items
  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add first page and ellipsis if needed
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => setCurrentPage(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
      }
    }

    // Add page numbers
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item 
          key={i} 
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    // Add last page and ellipsis if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => setCurrentPage(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  return (
    <Container className="mt-4">
      <ToastContainer />
      <h2 className="mb-4">Car Management System</h2>

      {/* Car Form */}
      <Form onSubmit={handleSubmit} className="mb-4">
        <Row className="align-items-end">
          <Col xs="8" md="9">
            <Form.Group controlId="formCarName">
              <Form.Label>{isEditing ? "Edit Car Name" : "Car Name"}</Form.Label>
              <Form.Control
                type="text"
                value={carName}
                onChange={(e) => setCarName(e.target.value)}
                placeholder="Enter car name"
              />
            </Form.Group>
          </Col>
          <Col xs="4" md="3" className="d-flex gap-2">
            <Button variant="primary" type="submit" className="flex-grow-1">
              {isEditing ? "Update" : "Add Car"}
            </Button>
            {isEditing && (
              <Button variant="secondary" onClick={handleCancelEdit}>
                Cancel
              </Button>
            )}
          </Col>
        </Row>
      </Form>

      {/* Search Filter */}
      <div className="mb-3">
        <InputGroup>
          <Form.Control
            placeholder="Search cars..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
          {searchTerm && (
            <Button 
              variant="outline-secondary"
              onClick={() => setSearchTerm("")}
            >
              Clear
            </Button>
          )}
        </InputGroup>
      </div>

      {/* Cars Table */}
      <div className="mt-4">
        <h3>Car List</h3>
        {loading ? (
          <p>Loading cars...</p>
        ) : filteredCars.length === 0 ? (
          <p>No cars found. {searchTerm ? "Try a different search term or " : ""}Add a new car.</p>
        ) : (
          <>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Car Name</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentCars.map((car, index) => (
                  <tr key={car.id}>
                    <td>{indexOfFirstCar + index + 1}</td>
                    <td>{car.car_name}</td>
                    <td>
                      <Button
                        variant="info"
                        size="sm"
                        className="me-2"
                        onClick={() => handleEdit(car)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => confirmDelete(car)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center">
              <div>
                Showing {indexOfFirstCar + 1}-{Math.min(indexOfLastCar, filteredCars.length)} of {filteredCars.length} cars
              </div>
              <Pagination>
                <Pagination.First 
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev 
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                />
                
                {renderPaginationItems()}
                
                <Pagination.Next 
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last 
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the car "{carToDelete?.car_name}"?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CarForm;